import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import { 
  Box, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getFAQs, deleteFAQ } from '../../services/faqService';

interface FAQ {
  faq_id: string | number;
  faq_question: string;
  faq_answer: string;
  faq_type: string;
  faq_status: 'Active' | 'Inactive';
  created_at?: string;
  updated_at?: string;
}

const FAQList: FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const response = await getFAQs();
      
      console.log('🔍 Full API response:', response);
      console.log('🔍 Response type:', typeof response);
      console.log('🔍 Response keys:', Object.keys(response || {}));
      
      // Try multiple possible data locations
      let faqData = null;
      
      if (response?.data?.data && Array.isArray(response.data.data)) {
        faqData = response.data.data;
        console.log('🔍 Using response.data.data (nested)');
      } else if (response?.data && Array.isArray(response.data)) {
        faqData = response.data;
        console.log('🔍 Using response.data (direct array)');
      } else if (Array.isArray(response)) {
        faqData = response;
        console.log('🔍 Using response (direct array)');
      } else if (response?.data?.faqs && Array.isArray(response.data.faqs)) {
        faqData = response.data.faqs;
        console.log('🔍 Using response.data.faqs');
      } else if (response?.faqs && Array.isArray(response.faqs)) {
        faqData = response.faqs;
        console.log('🔍 Using response.faqs');
      } else {
        console.log('🔍 Could not find FAQ array in response');
        console.log('🔍 Available data:', response);
        faqData = [];
      }
      
      console.log('🔍 Final FAQ data:', faqData);
      console.log('🔍 FAQ data length:', faqData?.length || 0);
      
      setFaqs(faqData || []);
    } catch (err) {
      setError('Failed to fetch FAQs. Please try again.');
      console.error('❌ Error fetching FAQs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (faq: FAQ) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;
    
    try {
      await deleteFAQ(faqToDelete.faq_id.toString());
      setFaqs(faqs.filter(faq => faq.faq_id !== faqToDelete.faq_id));
      setDeleteDialogOpen(false);
    } catch (err: any) {
      console.error('Error deleting FAQ:', err);
      setError(err.response?.data?.message || 'Failed to delete FAQ. Please try again.');
    }
  };

  if (loading && !faqs.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="container-xxl flex-grow-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        
        <Typography variant="h4"  fontWeight="bold">FAQs</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          component={Link}
          to="/faqs/new"
        >
          Add FAQ
        </Button>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Question</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faqs.length > 0 ? (
                faqs.map((faq) => (
                  <TableRow key={faq.faq_id}>
                    <TableCell>
                      <Typography variant="body1">{faq.faq_question}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={faq.faq_type || 'General'} 
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={faq.faq_status} 
                        color={faq.faq_status === 'Active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        component={Link} 
                        to={`/faqs/edit/${faq.faq_id}`}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleDeleteClick(faq)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No FAQs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the FAQ: <strong>{faqToDelete?.faq_question}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FAQList;
